import React, { useEffect, useState } from 'react';

import { cn } from 'lib/utils';
import { FlowThemeCanvas } from './FlowThemeCanvas';
import type { FlowThemeId, FlowThemeProgress } from './engine/types';
import { resolveFlowTheme } from './flowThemeRegistry';
import { maxPixelRatioForMode } from './renderBudget';
import { useFlowThemeRenderPolicy } from './useFlowThemeRenderPolicy';

export const PREVIEW_STEPS = 6;
const PREVIEW_STEP_MS = 1900;
const PREVIEW_STILL_STEP = 2;
const PREVIEW_START: FlowThemeProgress = { step: PREVIEW_STILL_STEP, total: PREVIEW_STEPS, complete: false };

/**
 * Walks a pretend flow forward on a timer: steps, a finale, then back to the
 * start (the scene rewinds). Resets to the still frame's step when inactive.
 */
export const useThemeDemoProgress = (active: boolean): FlowThemeProgress => {
  const [demo, setDemo] = useState<FlowThemeProgress>(PREVIEW_START);
  useEffect(() => {
    if (!active) {
      setDemo(PREVIEW_START);
      return undefined;
    }
    const timer = window.setInterval(() => {
      setDemo((previous) => {
        if (previous.complete) {
          return { step: 0, total: PREVIEW_STEPS, complete: false };
        }
        if (previous.step + 1 >= PREVIEW_STEPS) {
          return { ...previous, step: PREVIEW_STEPS, complete: true };
        }
        return { ...previous, step: previous.step + 1 };
      });
    }, PREVIEW_STEP_MS);
    return () => window.clearInterval(timer);
  }, [active]);
  return demo;
};

export interface FlowThemePreviewProps {
  themeId: FlowThemeId;
  /** Play the demo loop; otherwise a single representative frame is drawn. */
  animate?: boolean;
  className?: string;
}

/**
 * A small, self-contained render of a theme for pickers and galleries. It
 * follows the learner render policy (reduced motion, software rendering, a
 * slow device), so a theme that learners on this device would see still never
 * animates here either.
 */
export const FlowThemePreview: React.FC<FlowThemePreviewProps> = ({ themeId, animate = false, className }) => {
  const theme = resolveFlowTheme(themeId);
  const policy = useFlowThemeRenderPolicy(theme);
  const shouldAnimate = animate && policy.mode === 'animated';
  const demo = useThemeDemoProgress(shouldAnimate);
  const progress = shouldAnimate ? demo : PREVIEW_START;

  return (
    <div
      className={cn('relative overflow-hidden', className)}
      data-testid={`flow-theme-preview-${theme.id}`}
      data-flow-theme-animating={shouldAnimate ? 'true' : 'false'}
      data-flow-theme-render-mode={policy.mode}
      style={{ background: theme.swatch }}
    >
      {/* Never remount the canvas on hover: a tap's mousedown and mouseup must
          land on the same node or the browser drops the click on the card. */}
      <FlowThemeCanvas
        theme={theme}
        progress={progress}
        still={!shouldAnimate}
        lowQuality={policy.lowQuality}
        maxPixelRatio={maxPixelRatioForMode(policy.mode)}
        allowStillFallback={policy.allowStillFallback}
      />
    </div>
  );
};
